/**
 * FunnelEdges — SVG layer rendering connections between funnel nodes.
 * Draws curved Bezier paths with animated dashes and traffic labels.
 */
import type { FunnelNodeData, FunnelEdgeData } from "./funnel-types";

// ── Constants ──────────────────────────────────────────

const NODE_WIDTH = 180;
const NODE_HEADER_OFFSET = 30; // vertical offset for badge above node
const NODE_HEIGHT = 140;

// ── Helpers ────────────────────────────────────────────

const getNodeCenter = (node: FunnelNodeData): { x: number; y: number } => ({
  x: node.position.x + NODE_WIDTH / 2,
  y: node.position.y + NODE_HEADER_OFFSET + NODE_HEIGHT / 2,
});

const getNodeRight = (node: FunnelNodeData): { x: number; y: number } => ({
  x: node.position.x + NODE_WIDTH,
  y: node.position.y + NODE_HEADER_OFFSET + NODE_HEIGHT / 2,
});

const getNodeLeft = (node: FunnelNodeData): { x: number; y: number } => ({
  x: node.position.x,
  y: node.position.y + NODE_HEADER_OFFSET + NODE_HEIGHT / 2,
});

// ── Component ──────────────────────────────────────────

interface FunnelEdgesProps {
  nodes: FunnelNodeData[];
  edges: FunnelEdgeData[];
}

export default function FunnelEdges({ nodes, edges }: FunnelEdgesProps) {
  const nodeMap = new Map(nodes.map((n) => [n.id, n]));

  return (
    <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ zIndex: 5 }}>
      <defs>
        <marker
          id="arrowhead-conversion"
          markerWidth="8"
          markerHeight="6"
          refX="7"
          refY="3"
          orient="auto"
        >
          <polygon points="0 0, 8 3, 0 6" fill="rgba(52, 211, 153, 0.6)" />
        </marker>
        <marker
          id="arrowhead-dropoff"
          markerWidth="8"
          markerHeight="6"
          refX="7"
          refY="3"
          orient="auto"
        >
          <polygon points="0 0, 8 3, 0 6" fill="rgba(239, 62, 41, 0.4)" />
        </marker>
      </defs>

      {edges.map((edge) => {
        const fromNode = nodeMap.get(edge.fromNodeId);
        const toNode = nodeMap.get(edge.toNodeId);
        if (!fromNode || !toNode) return null;

        const start = getNodeRight(fromNode);
        const end = getNodeLeft(toNode);

        const dx = end.x - start.x;
        const curveOffset = Math.min(Math.abs(dx) * 0.4, 120);

        const path = `M ${start.x} ${start.y} C ${start.x + curveOffset} ${start.y}, ${end.x - curveOffset} ${end.y}, ${end.x} ${end.y}`;

        const isConversion = edge.edgeType === "conversion";
        const strokeColor = isConversion ? "rgba(52, 211, 153, 0.35)" : "rgba(239, 62, 41, 0.25)";
        const markerEnd = isConversion ? "url(#arrowhead-conversion)" : "url(#arrowhead-dropoff)";

        const midX = (start.x + end.x) / 2;
        const midY = (start.y + end.y) / 2;

        return (
          <g key={edge.id}>
            {/* Main path */}
            <path
              d={path}
              fill="none"
              stroke={strokeColor}
              strokeWidth={2}
              markerEnd={markerEnd}
              strokeDasharray="6 4"
            >
              <animate
                attributeName="stroke-dashoffset"
                from="0"
                to={isConversion ? "-20" : "20"}
                dur="2s"
                repeatCount="indefinite"
              />
            </path>

            {/* Traffic label */}
            <g transform={`translate(${midX}, ${midY})`}>
              <rect
                x={-20}
                y={-10}
                width={40}
                height={18}
                rx={9}
                fill="rgba(0,0,0,0.7)"
                stroke={isConversion ? "rgba(52, 211, 153, 0.3)" : "rgba(239, 62, 41, 0.2)"}
                strokeWidth={1}
              />
              <text
                x={0}
                y={3}
                textAnchor="middle"
                fill={isConversion ? "rgb(110, 231, 183)" : "rgb(239, 62, 41)"}
                fontSize={10}
                fontWeight={600}
                fontFamily="system-ui, sans-serif"
              >
                {edge.traffic.toLocaleString()}
              </text>
            </g>
          </g>
        );
      })}
    </svg>
  );
}
